import React from "react";
import { SketchPicker } from "react-color";
import { throttle } from "lodash";

import Button from "../Button";
import When from "../../utils/When";
import { hexToRgb } from "../../../utils/ColorUtils";
import AppConstants from "../../../constants/AppConstants";
import Palette from "./Palette";
import uiPrefix from "../../../utils/UiPrefixUtils";

const { BLACK, DEFAULT_OPACITY, DESELECT_PALETTE } = AppConstants;
const CONFIRM_LABEL = "OK";
const CANCEL_LABEL = "CANCEL";
const TRANSPARENT = "transparent";

const formatPickerColor = (color, alpha) => ({
  ...hexToRgb(color),
  a: alpha * 0.01,
});

const Picker = ({
  alpha,
  color,
  onCancel,
  onColorChange,
  onConfirm,
  paletteIndex,
  paletteOptions,
  setAlpha,
  setBaseColor,
  setPickerVisible,
  setShowPicker,
  showAlpha,
  showPalette,
  showPaletteOnly,
}) => {
  const _onThemeSelection = (paletteColor, i) => {
    setShowPicker(false);
    setPickerVisible(false);
    const index = i === 8 ? DESELECT_PALETTE : i;
    // zero if going to transparent, back to 100 if not selecting transparent and zero, otherwise use what is currently set
    const newAlpha = index === DESELECT_PALETTE ? 0 : alpha === 0 ? DEFAULT_OPACITY : alpha;
    setBaseColor(paletteColor);
    setAlpha(newAlpha);

    onColorChange(
      paletteColor,
      { baseColor: paletteColor, paletteIndex: index, alpha: newAlpha },
      true
    );
  };

  const _onPickerChange = throttle(({ rgb = {}, hex = BLACK }) => {
    const isTransparent = hex === TRANSPARENT;
    // scale alpha to 0-100
    const roundedAlpha = Math.round((rgb.a ?? 1) * DEFAULT_OPACITY);
    // remain zero if hex is transparent, or restore full opacity if alpha is zero to prevent unseen palette selections, otherwise use current alpha
    const newAlpha = isTransparent ? 0 : roundedAlpha === 0 ? DEFAULT_OPACITY : roundedAlpha;
    const newHex = isTransparent ? BLACK : hex;
    const colorWithAlpha = `${newHex}${newAlpha !== DEFAULT_OPACITY ? `:${newAlpha}` : ""}`;
    setAlpha(newAlpha);
    setBaseColor(newHex);

    onColorChange(
      colorWithAlpha,
      { baseColor: newHex, paletteIndex: DESELECT_PALETTE, alpha: newAlpha },
      false
    );
  }, 10);

  return (
    <>
      <When it={showPalette}>
        <Palette
          paletteOptions={paletteOptions}
          paletteIndex={paletteIndex}
          onSelection={_onThemeSelection}
        />
      </When>
      <When it={!showPaletteOnly}>
        <SketchPicker
          disableAlpha={!showAlpha}
          presetColors={[]}
          color={formatPickerColor(color, alpha)}
          onChange={_onPickerChange}
        />
        <div className="control-bar">
          <Button
            onClick={onCancel}
            look="link"
            data-e2e={uiPrefix("modal-colorpicker-cancel-btn")}
            className={uiPrefix("modal-colorpicker-cancel-btn")}
          >
            {CANCEL_LABEL}
          </Button>
          <Button
            onClick={onConfirm}
            look="primary"
            data-e2e={uiPrefix("modal-colorpicker-confirm-btn")}
            className={uiPrefix("modal-colorpicker-confirm-btn")}
          >
            {CONFIRM_LABEL}
          </Button>
        </div>
      </When>
    </>
  );
};

export default Picker;
